import DOMPurify from 'isomorphic-dompurify';
import { useMemo, useRef } from 'react';

import '@/styles/image-node.scss';
import '@/styles/list-node.scss';
import '@/styles/paragraph-node.scss';
import '@/styles/tiptap-editor.scss';

interface IProps {
  html?: string;
}
export default function ProductHtml({ html }: IProps) {
  const htmlRef = useRef<HTMLDivElement>(null);

  // [goto:*] 토큰을 부모 엘리먼트 id로 승격하는 전처리
  const processedHtml = useMemo(() => {
    const input = html || '';

    // SSR 환경에서는 간단 치환(기본 케이스)
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return input.replace(
        /<([a-zA-Z][\w:-]*)([^>]*)>(\s*)\[(?:goto):([^\]]+)\]/gi,
        (_m, tag, attrs, ws, id) => {
          // 이미 id가 있으면 그대로 두고 토큰만 제거하고 공백 보존
          if (/\sid=(["']).*?\1/.test(attrs)) {
            return `<${tag}${attrs}>${ws}`;
          }
          return `<${tag}${attrs} id="${id}">${ws}`;
        },
      );
    }

    // 클라이언트 환경: DOM 파싱으로 정확 처리
    const container = document.createElement('div');
    container.innerHTML = input;

    const walk = (el: Element) => {
      Array.from(el.children).forEach(child => {
        const first = child.firstChild;
        if (first && first.nodeType === Node.TEXT_NODE) {
          const text = first.textContent ?? '';
          const match = text.match(/^\s*\[goto:([^\]]+)\]/i);
          if (match) {
            const id = match[1];
            if (!child.getAttribute('id')) child.setAttribute('id', id);
            // 토큰만 제거하고 주변 공백/문자는 그대로 둠
            first.textContent = text.replace(/\[goto:[^\]]+\]/i, '');
          }
        }
        walk(child);
      });
    };
    walk(container);

    return container.innerHTML;
  }, [html]);

  // HTML sanitization 메모이제이션
  const sanitizedHtml = useMemo(() => {
    return processedHtml
      ? DOMPurify.sanitize(processedHtml, {
          ADD_ATTR: ['target', 'rel'],
        })
      : '';
  }, [processedHtml]);

  if (!html) return null;

  return (
    <div className='relative overflow-hidden'>
      <div
        ref={htmlRef}
        className='tiptap ProseMirror readonly'
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    </div>
  );
}
